/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import LegalbirdIoModal from "../../Modal/LegalbirdIoModal";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { UseForm } from "../../../types/UseForm";
import { FormContainer } from "react-hook-form-mui";
import { Grid, MenuItem } from "@mui/material";
import SelectElement from "../../ReactHookFormElements/SelectElement";
import { requiredFieldDefaultFieldBased } from "../../../services/reactHookFormValidationRules";
import { apiPost } from "../../../services/Api/apiCall";
import { useSnackbar } from "notistack";
import ButtonLoading from "../../Button/ButtonLoading";
import { ProductUnion } from "../../../types/ProductUnion";

type CreateAdministrativeLawCaseModalProps = {
  product: ProductUnion;
  open: boolean;
  handleClose: Function;
  refreshPage?: Function;
};

function CreateAdministrativeLawCaseModal({
  product,
  open,
  handleClose,
  refreshPage = () => {},
}: CreateAdministrativeLawCaseModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const initialValues = {
    caseType: null,
  };

  const formContext = useForm({
    defaultValues: initialValues,
  });

  const handleSubmit = async (values: UseForm["values"]) => {
    setIsLoading(true);

    let snackbarMessage = "Verfahren wurde erfolgreich angelegt";

    try {
      await apiPost(`/lb/services/administrative_law/${product.id}/create_case`, {
        caseType: values.caseType,
      });
    } catch (e) {
      snackbarMessage = "Verfahren konnte nicht erstellt werden";
    }

    enqueueSnackbar(snackbarMessage, {
      variant: "custom",
      isNonInteractive: true,
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "center",
      },
    });

    refreshPage();
    setIsLoading(false);
    handleClose();
  };

  if (product.productCategory !== "administrativeLaw") {
    return null;
  }

  return (
    <LegalbirdIoModal
      handleClose={handleClose}
      open={open}
      title={"Neues Verfahren anlegen"}
      submitButton={
        <FormContainer formContext={formContext} onSuccess={handleSubmit}>
          <ButtonLoading
            variant={"contained"}
            color={"primary"}
            type={"submit"}
            isLoading={isLoading}
            disabled={formContext.watch("caseType") === null}
          >
            Verfahren anlegen
          </ButtonLoading>
        </FormContainer>
      }
    >
      <p>
        Bitte wählen Sie, welches Verfahren angelegt werden soll.
        <br /> Wichtig: Das System erstellt daraufhin automatisch eine neue Akte. Das Hauptverfahren kann weiter
        bearbeitet werden.
      </p>
      <FormContainer formContext={formContext} onSuccess={handleSubmit}>
        <Grid container>
          <Grid size={12}>
            <SelectElement label={"Art des Verfahrens"} name={"caseType"} validation={requiredFieldDefaultFieldBased}>
              {product.linkedAdministrativeLawCaseIds["expeditedProceedingCaseId"] === null && (
                <MenuItem value={"expeditedProceedingCase"}>Eilverfahren</MenuItem>
              )}
              {product.linkedAdministrativeLawCaseIds["failureToActLawsuitCaseId"] === null && (
                <MenuItem value={"failureToActLawsuitCase"}>Untätigkeitsverfahren</MenuItem>
              )}
            </SelectElement>
          </Grid>
        </Grid>
      </FormContainer>
    </LegalbirdIoModal>
  );
}

export default CreateAdministrativeLawCaseModal;
